import { useRef } from 'react';
// store
import useCommonStore from 'store/useCommonStore';
// component
import TableCell, { ICommonCell } from 'components/common/table/cell/TableCell';
import PreviewPosting from 'components/PreviewPosting';
import style from 'styles/styled-components/style';
import CoreSvg from 'shared/CoreSvg';
import Icon from 'shared/icons';
// utils
import { gender_mapper, setAgeSection } from 'utils/match/matchFilterData';
import round, { floatFixed, roundFloat } from 'utils/round';
// type
import type { IGrid } from 'styles/styled-components/style';
import type { IMatchDB } from 'utils/dummy';
import type { IProfileCell } from 'components/common/table/cell/Profile';
import type { IIdCell } from 'components/common/table/cell/Id';
import styled from 'styled-components';
import TableGraphCellInner from 'components/common/table/TableGraphCellInner';
import { PlatformCode } from 'type/common';
import { useNavigate } from 'react-router-dom';

const useTable = () => {
	// store
	const { platform, column_width, preview_idx, setPreviewIdx, is_scroll } =
		useCommonStore();

	// ref
	const preview_list_ref = useRef<HTMLDivElement[]>([]);

	const navigate = useNavigate();

	/** cell_type이 number인 cell */
	const numberCell = (value: number | null, unit: string, idx: number) => {
		const result = value === null ? '-' : `${round(value, 'down')} ${unit}`;
		return (
			<TableCell _width={column_width[idx]} cell_type="number">
				{result}
			</TableCell>
		);
	};
	/** cell_type이 percent인 cell */
	const percentCell = (value: number | null, idx: number) => {
		const result =
			value === null ? '-' : floatFixed(roundFloat(value, 'down', 2), 2) + '%';
		return (
			<TableCell _width={column_width[idx]} cell_type="number">
				{result}
			</TableCell>
		);
	};

	/** 리포트 모달 열기 */
	const openReport = (row_data: IMatchDB) => {
		const params = new URLSearchParams(location.search);
		params.append('rm', 'm');
		params.append('pk', row_data?.platform_pk as string);
		params.append('p', platform.code as PlatformCode);
		navigate({ search: params.toString() });
	};

	// 리턴할 객체
	const match_cell_mapper: Record<
		string,
		(data: IMatchDB, idx: number, row_idx: number) => JSX.Element
	> = {
		number(data, idx, row_idx) {
			return (
				<TableCell _width={column_width[idx]} _padding="0">
					<style.Grid _width="24px" flex="center">
						<style.Text typo="span_bold">{data.ranking}</style.Text>
					</style.Grid>
				</TableCell>
			);
		},
		profile(data, idx) {
			const props: IProfileCell = {
				_width: column_width[idx],
				profile_url: data.profile_url,
				username: data.username,
			};

			return <TableCell.Profile {...props} />;
		},
		username(data, idx) {
			const props: IIdCell = {
				_width: column_width[idx],
				username: data.username,
				full_name: data.full_name,
			};

			return <TableCell.Id {...props} />;
		},
		icons: (data, idx, row_idx) => {
			const cell_props: ICommonCell = {
				_width: column_width[idx],
			};

			const container_props: IGrid = {
				flex: 'start',
				_gap: '16px',
			};

			const platform_preview_mapper = {
				ig: <Icon.Picture />,
				yt: <Icon.Video />,
				nb: <Icon.Picture />,
				tt: <Icon.Video />,
				none: <></>,
			};

			return (
				<TableCell {...cell_props}>
					<style.Grid {...container_props}>
						<IconBox data-dd-action-name="open report at IMMS">
							<CoreSvg _onClick={() => openReport(data)}>
								<Icon.Search />
							</CoreSvg>
						</IconBox>

						<IconBox>
							<a
								href={data?.url}
								target="_blank"
								rel="noreferrer"
								onClick={(e) => e.stopPropagation()}
							>
								<CoreSvg>
									<Icon.OutLink />
								</CoreSvg>
							</a>
						</IconBox>

						<IconBox>
							<div
								ref={(e: HTMLDivElement) => (preview_list_ref.current[row_idx] = e)}
								onMouseEnter={() => setPreviewIdx(row_idx)}
								onMouseLeave={() => setPreviewIdx(null)}
							>
								<CoreSvg>{platform_preview_mapper[platform.code ?? 'none']}</CoreSvg>
								{!is_scroll && (
									<PreviewPosting
										platform_pk={data.platform_pk as string}
										is_displayed={row_idx === preview_idx}
										target={preview_list_ref?.current[row_idx]}
									/>
								)}
							</div>
						</IconBox>
					</style.Grid>
				</TableCell>
			);
		},
		category(data, idx) {
			const categories = [data.category_1, data.category_2];
			return <TableCell.Category _width={column_width[idx]} categories={categories} />;
		},
		audience(data, idx) {
			const result =
				data.audience_age === null || data.audience_gender === null
					? '분석 전'
					: !Number.isNaN(+data.audience_gender)
					? `${setAgeSection(data.audience_age)} ${
							+data.audience_gender >= 0.5 ? '여성' : '남성'
					  }`
					: `${setAgeSection(data.audience_age)} ${gender_mapper[data.audience_gender]}`;
			return <TableCell _width={column_width[idx]}>{result}</TableCell>;
		},
		follower_count: (data, idx) => numberCell(data.follower_count, '명', idx),
		real_follower_count: (data, idx) => numberCell(data.real_follower_count, '명', idx),
		reach_score: (data, idx) =>
			numberCell(data.reach_score, platform.code === 'ig' ? '명' : '회', idx),
		sub_video_rate: (data, idx) =>
			percentCell(
				data.sub_video_rate ? data.sub_video_rate * 100 : data.sub_video_rate,
				idx,
			),
		real_engagement: (data, idx) => percentCell(data.real_engagement, idx),
		post_count: (data, idx) => numberCell(data.post_count, '개', idx),
		total_visitor: (data, idx) => numberCell(data.total_visitor, '명', idx),
		avg_today_visitor: (data, idx) => numberCell(data.avg_today_visitor, '명', idx),
		growth(data, idx) {
			return (
				<TableCell _width={column_width[idx]} cell_type="center">
					<TableGraphCellInner growth_list={data.growth_list ?? []} />
				</TableCell>
			);
		},
		follower_growth_list(data, idx) {
			return (
				<TableCell _width={column_width[idx]} cell_type="center">
					<TableGraphCellInner growth_list={data.follower_growth_list ?? []} />
				</TableCell>
			);
		},
		follower_growth_rate(data, idx) {
			const setColorType = () => {
				if (+data?.follower_growth_rate > 0) return 'red';
				if (+data?.follower_growth_rate === 0) return 'black';
				if (+data?.follower_growth_rate < 0) return 'blue';
				else return 'black';
			};

			const arrow_mapper = {
				red: '▲ ',
				blue: '▼ ',
				black: <></>,
			};

			const follower_growth_rate =
				data?.follower_growth_rate !== null && data?.follower_growth_rate !== undefined
					? floatFixed(roundFloat(data?.follower_growth_rate, 'down', 2), 2) + '%'
					: '-';
			const follower_growth =
				data?.follower_growth !== null && data?.follower_growth !== undefined
					? roundFloat(data?.follower_growth, 'down', 0).toLocaleString()
					: '-';

			return (
				<TableCell _width={column_width[idx]}>
					<ColoredText color_type={setColorType()}>
						{arrow_mapper[setColorType()]}
						{follower_growth_rate}
						<span>({follower_growth})</span>
					</ColoredText>
				</TableCell>
			);
		},
	};

	return match_cell_mapper;
};

const ColoredText = styled.p<{ color_type: 'blue' | 'red' | 'black' }>`
	color: ${({ theme, color_type }) => {
		const color_mapper: Record<string, string> = {
			blue: theme.colors.system.blue,
			red: theme.colors.system.red,
			black: theme.colors.gray.gray80_text,
		};

		return color_mapper[color_type];
	}};
	${({ theme }) => theme.typo.body.span_bold};

	& > span {
		color: ${({ theme }) => theme.colors.gray.gray80_text};
		${({ theme }) => theme.typo.body.span};
	}
`;

const IconBox = styled.div`
	width: 24px;
	height: 24px;
	position: relative;
	border-radius: 50%;
	cursor: pointer;

	position: relative;

	& span {
		position: relative;
		z-index: 1;
	}

	&::before {
		content: '';
		display: block;
		width: 24px;
		height: 24px;
		border-radius: 50%;
		cursor: pointer;
		transition: 0.2s;

		position: absolute;
		top: 0;
		left: 0;

		z-index: 0;
	}

	&:hover {
		&::before {
			content: '';
			display: block;
			width: 24px;
			height: 24px;
			border-radius: 50%;
			cursor: pointer;
			transition: 0.2s;

			position: absolute;
			top: 0;
			left: 0;

			z-index: 0;
			background-color: ${({ theme }) => theme.colors.gray.gray05_bg};
			transform: scale(1.5);
		}
	}
`;

export default useTable;
